import { ExclamationMarkIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { Color, Style } from '../constants';
import { color } from '../constants/color.css';
import Text from './Text';

type Props = {
  body: string;
  actions: React.ReactNode;
  fullscreen?: boolean;
  reference?: string | null;
};

/** Shared layout for the page error and the error-boundary fallback. */
export default function ErrorState({
  body,
  actions,
  fullscreen,
  reference,
}: Props) {
  const headlineRef = React.useRef<HTMLHeadingElement>(null);

  React.useEffect(() => {
    headlineRef.current?.focus();
  }, []);

  return (
    <html.div
      role="alert"
      style={[
        Style.col,
        Style.centerBoth,
        styles.wrap,
        fullscreen && styles.fullscreen,
      ]}
    >
      <html.div style={[Style.centerBoth, styles.iconCircle]}>
        <ExclamationMarkIcon size={24} color={Color.accent} />
      </html.div>
      <html.h2 ref={headlineRef} tabIndex={-1} style={styles.headlineWrap}>
        <Text style={styles.headline}>Something went wrong</Text>
      </html.h2>
      <Text as="p" style={styles.body}>
        {body}
      </Text>
      <html.div style={[Style.row, Style.alignCenter, styles.actions]}>
        {actions}
      </html.div>
      {reference && (
        <Text typeface="caption.medium" color="neutral40">
          {reference}
        </Text>
      )}
    </html.div>
  );
}

const styles = css.create({
  wrap: {
    gap: 12,
    minHeight: '60vh',
    paddingBlock: 24,
    paddingInline: 16,
    textAlign: 'center',
  },
  fullscreen: {
    minHeight: '100vh',
    backgroundColor: color.neutral5,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: color.accentBg,
  },
  headlineWrap: {
    margin: 0,
  },
  headline: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 1.4,
    color: color.neutral100,
  },
  body: {
    margin: 0,
    maxWidth: 440,
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral60,
  },
  actions: {
    gap: 8,
  },
});
